import { ResultsMode } from 'interfaces/experimentResults';
import { ResultsExportType } from 'services/export/exportTypes';
import { ResultsExportContext } from './useResultsExportContext';

export interface ResultsExportChoiceAvailability {
  disabled: boolean;
  // Why the choice cannot be used, or what Continue will expand the export to.
  note?: string;
}

type AvailabilityContext = Pick<
  ResultsExportContext,
  | 'study'
  | 'resultsMode'
  | 'orchestrationId'
  | 'isSlidesMode'
  | 'hasSpecificSelections'
  | 'hasIntermediateResultsSelection'
  | 'areFiltersAppliedExceptStudy'
  | 'canDownloadIntermediateResults'
  | 'canDownloadCellsTable'
>;

const unavailable = (note: string): ResultsExportChoiceAvailability => ({ disabled: true, note });

const noExportAllowed = 'Exports are disabled for this study.';

export const getResultsExportChoiceAvailability = (
  type: ResultsExportType,
  context: AvailabilityContext
): ResultsExportChoiceAvailability => {
  const { study } = context;
  if (!study?.allowExport) return unavailable(noExportAllowed);

  const isManualWithoutOrchestration = context.resultsMode === ResultsMode.Manual && !context.orchestrationId;

  switch (type) {
    case 'published': {
      if (!study.hasResults) return unavailable('No selection is valid: this study has no results to export.');
      if (context.hasSpecificSelections || context.areFiltersAppliedExceptStudy) {
        const current = context.hasSpecificSelections
          ? `the selected ${context.isSlidesMode ? 'slides' : 'cases'}`
          : 'the current filters';
        return { disabled: false, note: `Continuing expands the export from ${current} to the whole study.` };
      }
      return { disabled: false };
    }
    case 'bundle':
      return study.hasResults
        ? { disabled: false }
        : unavailable('No selection is valid: this study has no results to export.');
    case 'original':
      if (!context.canDownloadCellsTable) return unavailable('You do not have permission to download cell tables.');
      if (!study.hasResults) return unavailable('No selection is valid: this study has no results to export.');
      if (isManualWithoutOrchestration) return unavailable('Select an orchestration to enable cell-table export.');
      return { disabled: false };
    case 'intermediate':
      if (!context.canDownloadIntermediateResults) {
        return unavailable('You do not have permission to download intermediate results.');
      }
      if (!context.hasIntermediateResultsSelection) {
        return unavailable(`Select specific ${context.isSlidesMode ? 'slides' : 'cases'} first.`);
      }
      return { disabled: false };
    case 'features':
      if (context.resultsMode === ResultsMode.Published && !study.hasFeatures) {
        return unavailable('No selection is valid: this study has no features to export.');
      }
      if (isManualWithoutOrchestration) return unavailable('Select an orchestration to export features.');
      return { disabled: false };
    case 'polygons':
      return study.hasFeatures
        ? { disabled: false }
        : unavailable('No selection is valid: this study has no selection or exclusion polygons.');
    case 'metadata':
    default:
      return { disabled: false };
  }
};
